/**
 * VMSMan Frontend - Main Entry Point
 *
 * This file exports all modules and initializes the application.
 */

// Vendor setup - must be first to ensure jQuery and plugins are available globally
import './vendor/plugins.js';
import '../vmsman.css';

// Re-export API client
export { api, ApiClient } from './api/client.js';
export * from './api/types.js';

// Re-export utilities
export { escapeHtml, escapeAttr, formatBytes, formatDuration } from './utils.js';

// Re-export global state
export { vms, setVms, getVmByName, suspendUpdates, setSuspendUpdates } from './globals.js';

// Re-export UI helpers
export { declareLoading, declareLoaded, notificationDialog, confirmDialog, errorDialog } from './ui/dialogs.js';
export { showHelpDialog } from './ui/help.js';
export {
  formSubmitHandler,
  generateButton,
  generateSplitButton,
  generateComboSelect,
  generateMultiSelect,
  getSelectedOptions,
  generateTextInput,
  generateCheckbox,
} from './ui/forms.js';

// Re-export VM modules
export { vmStart, vmStop, vmRestart, vmForceRestart, vmForcePowerOff, vmPause, vmResume, vmDelete, vmConnectVnc, vmOpenMonitor, vmPortForwards } from './vm/actions.js';

export { renderVmTable } from './vm/table.js';
export { vmModifyOrCreate, vmModifyEnlightenments } from './vm/modify.js';
export { openDrivesDialog, getCurrentDrives, resetDriveState } from './vm/drives.js';
export { openCpuOptionsDialog, getCurrentCpuConfig, resetCpuState } from './vm/cpu.js';
export { showRuntimePanel } from './vm/runtime.js';

// VNC console and monitor terminal are NOT eagerly exported here.
// They use libraries (noVNC, xterm) that access document.body at module load
// time, which fails when the bundle is loaded in <head>.
// These modules are dynamically imported where needed (see vm/actions.ts).

// Re-export user settings
export { userSettings, setPassword, doLogout } from './user/settings.js';

// Re-export admin modules
export { adminSettings } from './admin/settings.js';
export {
  fillUsersTable,
  createUser,
  modifyUser,
  deleteUser,
  setEmail,
  resetUserPassword,
  resendUserInvite,
} from './admin/users.js';
export {
  fillPoolsTable,
  fillDisksTable as fillStorageDisksTable,
  fillDrivesTable,
  createPool,
  deletePool,
  cloneDisk,
  resizeDisk,
} from './admin/storage.js';
export {
  fillHostsTable,
  createHost,
  modifyHost,
  deleteHost,
  testHost,
  copyPublicKey,
} from './admin/hosts.js';
export {
  fillOrganizationsTable,
  createOrganization,
  modifyOrganization,
  deleteOrganization,
} from './admin/organizations.js';

// Re-export host resources
export {
  connectHostMonitor,
  disconnectHostMonitor,
  setResourceCallsDisabled,
  connectRemoteHostMonitors,
} from './host/resources.js';

// Re-export app initialization
export {
  getCurrentUser,
  isAuthenticated,
  hasPermission,
  initAuthenticatedPage,
  initPublicPage,
  getLoginRedirect,
  showLoading,
  hideLoading,
} from './app/init.js';
export { applyPermissions, applyPermissionsToForm } from './app/permissions.js';
export { injectLayout, updateConnectionInfo } from './app/layout.js';

// Re-export page modules
export { initLoginPage, showForgotPassword, showLoginForm } from './pages/login.js';
export { init2faPage } from './pages/twofa.js';
export {
  initMainPage,
  goToVms,
  goToDiskman,
  goToUsers,
  goToNetworks,
  goToStorage,
  goToHosts,
  goToOrganizations,
  goToEvents,
  vpnAccess,
} from './pages/main.js';
export { initEventsPage } from './pages/events.js';
export { initVmsPage, startAllVms, stopAllVms, cleanupVmsPage } from './pages/vms.js';
export { initUsersPage } from './pages/users.js';
export { initNetworksPage } from './pages/networks.js';
export { initHostsPage } from './pages/hosts.js';
export { initOrganizationsPage } from './pages/organizations.js';
export { initStoragePage } from './pages/storage.js';
export { initInvitePage } from './pages/invite.js';
export { initResetPasswordPage } from './pages/reset-password.js';
export { initDiskmanPage, fillDisksTable, cleanupDiskmanPage } from './pages/diskman.js';

// Initialize application when DOM is ready
if (typeof window !== 'undefined') {
  // The actual initialization depends on which page is loaded
  // Each page will import and call the appropriate init function

  // Expose urlParam helper for backward compatibility
  ($ as JQueryStatic & { urlParam: (name: string) => string | null }).urlParam = function (
    name: string
  ): string | null {
    const results = new RegExp('[\\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results === null) {
      return null;
    } else {
      return results[1] || null;
    }
  };
}
